import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { fetchProducts, getAllProducts } from "../../../../redux/actions/product";
import { fetchProfiles } from "../../../../redux/actions/profile";
import profilesJSON from "../../../../json/profile.json";
import { analyzeSingleProfileMedication } from "../../../../helpers/multiparametricAnalysisUtils";
import "../../../../assets/css/multiparametric-analysis.css";

class MultiparametricAnalysisPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedProfiles: [],
            selectedMedications: [],
            profileOptions: [],
            medicationOptions: [],
            medicationsLoading: false,
            medicationsError: false,
            analysisResult: null,
            isAnalyzing: false,
            profileSearchText: '',
            medicationSearchText: '',
            showProfileDropdown: false,
            showMedicationDropdown: false,
            showAlternatives: false,
            isLoading: false,
            error: null
        };

        this.handleProfileSelection = this.handleProfileSelection.bind(this);
        this.handleMedicationSelection = this.handleMedicationSelection.bind(this);
        this.performAnalysis = this.performAnalysis.bind(this);
        this.exportResults = this.exportResults.bind(this);
        this.newAnalysis = this.newAnalysis.bind(this);
        this.toggleAlternatives = this.toggleAlternatives.bind(this);
        this.handleMedicationInputFocus = this.handleMedicationInputFocus.bind(this);
        this.handleMedicationInputBlur = this.handleMedicationInputBlur.bind(this);
        this.handleMedicationDropdownMouseDown = this.handleMedicationDropdownMouseDown.bind(this);
        this.handleProfileInputFocus = this.handleProfileInputFocus.bind(this);
        this.handleProfileInputBlur = this.handleProfileInputBlur.bind(this);
        this.handleProfileDropdownMouseDown = this.handleProfileDropdownMouseDown.bind(this);
        this.handleNoMedications = this.handleNoMedications.bind(this);
        this.medicationDropdownClicked = false;
        this.profileDropdownClicked = false;

        this.profileInputRef = React.createRef();
        this.medicationInputRef = React.createRef();
        this.profileDropdownRef = React.createRef();
        this.medicationDropdownRef = React.createRef();
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        try {
            const productsChanged = prevProps.products !== this.props.products && this.props.products && this.props.products.length > 0;
            const profilesChanged = prevProps.profiles !== this.props.profiles && this.props.profiles && this.props.profiles.length > 0;

            if (productsChanged) {
                this.setState({
                    medicationOptions: this.props.products || []
                });
            }

            if (profilesChanged) {
                const profileOptions = (this.props.profiles || []).map(profile => ({
                    id: profile.id || profile._id,
                    nom: profile.nom || profile.name || profile.profile,
                    profile: profile.profile || profile.nom || profile.name
                }));
                this.setState({ profileOptions });
            }

            if (this.state.medicationOptions.length === 0 && this.props.products && this.props.products.length > 0) {
                this.setState({
                    medicationOptions: this.props.products
                });
            }
        } catch (error) {
            this.setState({ error: error.message });
        }
    }

    async loadData() {
        this.setState({ error: null });

        try {
            await this.loadProfiles();
            await this.loadMedications();
        } catch (error) {
            this.setState({
                error: "Impossible de charger les données. Veuillez réessayer."
            });
            this.props.showNotification("Erreur lors du chargement des données", "danger");
        }
    }

    async loadProfiles() {
        try {
            if (this.props.profiles && this.props.profiles.length > 0) {
                const profileOptions = this.props.profiles.map(profile => ({
                    id: profile.id || profile._id,
                    nom: profile.nom || profile.name || profile.profile,
                    name: profile.nom || profile.name || profile.profile,
                    profile: profile.profile || profile.nom || profile.name
                }));
                this.setState({ profileOptions });
                return;
            }

            const profiles = await this.props.fetchProfiles({ limit: 1000 });

            if (profiles && profiles.length > 0) {
                const profileOptions = profiles.map(profile => ({
                    id: profile.id || profile._id,
                    nom: profile.nom || profile.name || profile.profile,
                    name: profile.nom || profile.name || profile.profile,
                    profile: profile.profile || profile.nom || profile.name
                }));
                this.setState({ profileOptions });
            } else {
                const profileOptions = profilesJSON.map(profile => ({
                    id: profile.id,
                    nom: profile.profile,
                    name: profile.profile,
                    profile: profile.profile
                }));
                this.setState({ profileOptions });
            }
        } catch (error) {
            const profileOptions = profilesJSON.map(profile => ({
                id: profile.id,
                nom: profile.profile,
                name: profile.profile,
                profile: profile.profile
            }));
            this.setState({ profileOptions });
        }
    }

    async loadMedications() {
        this.setState({ medicationsLoading: true, medicationsError: false });
        try {
            if (this.props.products && this.props.products.length > 0) {
                this.setState({
                    medicationOptions: this.props.products,
                    medicationsLoading: false
                });
                return;
            }

            if (!this.props.fetchProducts) {
                this.handleNoMedications();
                return;
            }

            const result = await this.props.fetchProducts({ limit: 1000 });
            const medications = Array.isArray(result) ? result : (result?.products || []);

            if (medications && medications.length > 0) {
                this.setState({
                    medicationOptions: medications,
                    medicationsLoading: false
                });
            } else {
                this.handleNoMedications();
            }
        } catch (error) {
            this.setState({ medicationsError: true });
            this.handleNoMedications();
        }
    }

    handleNoMedications() {
        this.setState({
            medicationOptions: [],
            medicationsLoading: false
        });
    }

    analyzeProfileMedicationPair(profile, medication) {
        if (!medication) {
            return { isContraindicated: false, details: ['Médicament non disponible'] };
        }

        const availableProfiles = this.props.profiles || this.state.profileOptions || profilesJSON;
        const analysis = analyzeSingleProfileMedication(profile, medication, availableProfiles);

        return {
            isContraindicated: analysis.isContraindicated,
            details: analysis.details,
            warnings: analysis.warnings,
            severity: analysis.severity
        };
    }

    findAlternatives(contraindicatedMedication, profile, allMedications) {
        if (!contraindicatedMedication || !allMedications) return [];

        const alternatives = [];

        const equivalenceCriteria = {
            dci: contraindicatedMedication.DCI || contraindicatedMedication.dci || contraindicatedMedication.substance,
            forme: contraindicatedMedication.forme,
            dosage: contraindicatedMedication.dosage,
            famille: contraindicatedMedication.famille || contraindicatedMedication.familleTherapeutique,
            classe: contraindicatedMedication.classe || contraindicatedMedication.classePharmacologique
        };

        for (const medication of allMedications) {
            if (!medication || medication._id === contraindicatedMedication._id || medication.id === contraindicatedMedication.id) {
                continue;
            }

            const alternativeAnalysis = this.analyzeProfileMedicationPair(profile, medication);
            if (alternativeAnalysis.isContraindicated) {
                continue;
            }

            let alternativeType = null;
            let score = 0;

            if (equivalenceCriteria.dci && equivalenceCriteria.forme && equivalenceCriteria.dosage) {
                const medicationDCI = medication.DCI || medication.dci || medication.substance;
                const isSameDCI = medicationDCI &&
                    medicationDCI.toLowerCase().trim() === equivalenceCriteria.dci.toLowerCase().trim();

                const isSameForme = medication.forme &&
                    medication.forme.toLowerCase().trim() === equivalenceCriteria.forme.toLowerCase().trim();

                const isSameDosage = medication.dosage &&
                    medication.dosage.toLowerCase().trim() === equivalenceCriteria.dosage.toLowerCase().trim();

                if (isSameDCI && isSameForme && isSameDosage) {
                    alternativeType = 'equivalent';
                    score = 100;
                }
            }

            if (!alternativeType) {
                const medicationFamille = medication.famille || medication.familleTherapeutique;
                const medicationClasse = medication.classe || medication.classePharmacologique;

                if (equivalenceCriteria.famille && medicationFamille &&
                    medicationFamille.toLowerCase().trim() === equivalenceCriteria.famille.toLowerCase().trim()) {
                    alternativeType = 'meme_famille';
                    score = 80;
                }
                else if (equivalenceCriteria.classe && medicationClasse &&
                    medicationClasse.toLowerCase().trim() === equivalenceCriteria.classe.toLowerCase().trim()) {
                    alternativeType = 'meme_classe';
                    score = 70;
                }
                else if (this.hasCommonIndications(contraindicatedMedication, medication)) {
                    alternativeType = 'indication_similaire';
                    score = 60;
                }
            }

            if (alternativeType && score > 0) {
                const medicationFamille = medication.famille || medication.familleTherapeutique;
                const medicationClasse = medication.classe || medication.classePharmacologique;

                alternatives.push({
                    nom: medication.nom || medication.name || 'Médicament sans nom',
                    DCI: medication.DCI || medication.dci || medication.substance || '',
                    type: alternativeType,
                    score: score
                });
            }
        }

        return alternatives
            .sort((a, b) => b.score - a.score)
            .slice(0, 8);
    }

    hasCommonIndications(med1, med2) {
        try {
            const indications1 = (med1.indications || '').toLowerCase().trim();
            const indications2 = (med2.indications || '').toLowerCase().trim();

            if (!indications1 || !indications2 || indications1.length < 10 || indications2.length < 10) {
                return false;
            }

            const keywords1 = indications1.split(/[\s,;.]+/).filter(word => word.length > 4);
            const keywords2 = indications2.split(/[\s,;.]+/).filter(word => word.length > 4);

            const commonKeywords = keywords1.filter(keyword =>
                keywords2.some(k2 => k2.includes(keyword) || keyword.includes(k2))
            );

            return commonKeywords.length >= 2;
        } catch (error) {
            return false;
        }
    }

    handleProfileSelection(profile) {
        try {
            if (!profile) return;

            const { selectedProfiles } = this.state;
            const profileName = profile.nom || profile.name || profile.profile || (typeof profile === 'string' ? profile : '');

            if (!profileName) return;

            if (selectedProfiles.includes(profileName)) {
                this.setState({
                    selectedProfiles: selectedProfiles.filter(p => p !== profileName)
                });
            } else {
                this.setState({
                    selectedProfiles: [...selectedProfiles, profileName]
                });
            }

            this.setState({
                showProfileDropdown: false,
                profileSearchText: ''
            });
        } catch (error) {
            // Gérer l'erreur silencieusement
        }
    }

    removeProfile(profileName) {
        this.setState({
            selectedProfiles: this.state.selectedProfiles.filter(p => p !== profileName)
        });
    }

    handleMedicationSelection(medication) {
        try {
            const { selectedMedications } = this.state;
            if (!medication) return;

            const medicationId = medication._id || medication.id;

            if (selectedMedications.find(m => (m._id || m.id) === medicationId)) {
                this.setState({
                    selectedMedications: selectedMedications.filter(m => (m._id || m.id) !== medicationId)
                });
            } else {
                this.setState({
                    selectedMedications: [...selectedMedications, medication]
                });
            }

            this.setState({
                showMedicationDropdown: false,
                medicationSearchText: ''
            });
            this.medicationDropdownClicked = false;
        } catch (error) {
            // Gérer l'erreur silencieusement
        }
    }

    removeMedication(medicationId) {
        this.setState({
            selectedMedications: this.state.selectedMedications.filter(m => (m._id || m.id) !== medicationId)
        });
    }

    handleMedicationInputFocus(e) {
        this.setState({
            showMedicationDropdown: true
        });

        if (!this.state.medicationOptions || this.state.medicationOptions.length === 0) {
            this.loadMedications().catch(() => {});
        }

        if (e.target) {
            e.target.style.borderColor = 'var(--app-primary-color)';
        }
    }

    handleMedicationInputBlur(e) {
        setTimeout(() => {
            if (!this.medicationDropdownClicked) {
                this.setState({
                    showMedicationDropdown: false
                });
            }
            this.medicationDropdownClicked = false;
        }, 200);
        if (e.target) {
            e.target.style.borderColor = '#d1d5db';
        }
    }

    handleMedicationDropdownMouseDown() {
        this.medicationDropdownClicked = true;
    }

    handleProfileInputFocus(e) {
        this.setState({
            showProfileDropdown: true
        });

        if (!this.state.profileOptions || this.state.profileOptions.length === 0) {
            this.loadProfiles().catch(() => {});
        }

        if (e.target) {
            e.target.style.borderColor = 'var(--app-primary-color)';
        }
    }

    handleProfileInputBlur(e) {
        setTimeout(() => {
            if (!this.profileDropdownClicked) {
                this.setState({
                    showProfileDropdown: false
                });
            }
            this.profileDropdownClicked = false;
        }, 300);
        if (e.target) {
            e.target.style.borderColor = '#d1d5db';
        }
    }

    handleProfileDropdownMouseDown = (e) => {
        e.preventDefault();
        this.profileDropdownClicked = true;
    }

    performAnalysis() {
        try {
            const { selectedProfiles, selectedMedications, medicationOptions } = this.state;

            if (selectedProfiles.length === 0) {
                this.props.showNotification("Veuillez sélectionner au moins un profil", "warning");
                return;
            }

            if (selectedMedications.length === 0) {
                this.props.showNotification("Veuillez sélectionner au moins un médicament", "warning");
                return;
            }

            this.setState({ isAnalyzing: true });

            setTimeout(() => {
                const analysisResult = [];
                const alternatives = {};

                for (const profile of selectedProfiles) {
                    for (const medication of selectedMedications) {
                        const result = this.analyzeProfileMedicationPair(profile, medication);

                        analysisResult.push({
                            profil: profile,
                            medicament: medication.nom || medication.name || 'Médicament sans nom',
                            resultat: result.isContraindicated ? 'contre-indiqué' : 'non contre-indiqué'
                        });

                        if (result.isContraindicated) {
                            const alternativesList = this.findAlternatives(medication, profile, medicationOptions);
                            alternatives[medication.nom || medication.name || 'Médicament sans nom'] = {
                                profil_concerne: profile,
                                medicament_original: {
                                    nom: medication.nom || medication.name,
                                    DCI: medication.DCI || medication.dci,
                                    forme: medication.forme,
                                    dosage: medication.dosage,
                                    fabricant: medication.fabricant
                                },
                                famille: medication.famille || medication.familleTherapeutique || '',
                                classe: medication.classe || medication.classePharmacologique || '',
                                equivalents_disponibles: alternativesList
                            };
                        }
                    }
                }

                const finalResult = {
                    analyse: analysisResult,
                    alternatives: alternatives,
                    timestamp: new Date().toISOString()
                };

                this.setState({
                    analysisResult: finalResult,
                    isAnalyzing: false
                });

                this.props.showNotification(
                    `Analyse terminée: ${analysisResult.length} combinaisons analysées`,
                    "success"
                );
            }, 1500);
        } catch (error) {
            this.setState({ isAnalyzing: false });
            this.props.showNotification("Erreur lors de l'analyse", "danger");
        }
    }

    newAnalysis() {
        this.setState({
            selectedProfiles: [],
            selectedMedications: [],
            analysisResult: null,
            profileSearchText: '',
            medicationSearchText: '',
            showAlternatives: false
        });
    }

    toggleAlternatives() {
        this.setState({
            showAlternatives: !this.state.showAlternatives
        });
    }

    exportResults() {
        try {
            const { analysisResult } = this.state;
            if (!analysisResult) return;

            const dataStr = JSON.stringify(analysisResult, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

            const exportFileDefaultName = `analyse_multiparametrique_${new Date().toISOString().split('T')[0]}.json`;

            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', exportFileDefaultName);
            linkElement.click();
        } catch (error) {
            // Gérer l'erreur silencieusement
        }
    }

    render() {
        const {
            selectedProfiles,
            selectedMedications,
            isAnalyzing,
            analysisResult,
            profileOptions,
            medicationOptions,
            profileSearchText,
            medicationSearchText,
            showProfileDropdown,
            showMedicationDropdown,
            error
        } = this.state;

        const filteredProfiles = (profileOptions || []).filter(profile => {
            if (!profile) return false;

            const profileName = (
                profile.nom ||
                profile.name ||
                profile.profile ||
                (typeof profile === 'string' ? profile : '')
            ).toLowerCase();

            const searchTerm = (profileSearchText || '').toLowerCase().trim();
            const profileIdentifier = profile.nom || profile.name || profile.profile || profile;
            const isAlreadySelected = selectedProfiles.includes(profileIdentifier);

            if (!searchTerm) {
                return !isAlreadySelected;
            }

            return profileName.includes(searchTerm) && !isAlreadySelected;
        }).slice(0, 10);

        const filteredMedications = (medicationOptions || []).filter(med => {
            if (!med) return false;

            const medName = (
                med.nom ||
                med.name ||
                med.nomCommercial ||
                med.DCI ||
                ''
            ).toLowerCase();

            const searchTerm = (medicationSearchText || '').toLowerCase().trim();
            const isAlreadySelected = selectedMedications.find(m =>
                m && (m._id || m.id) === (med._id || med.id)
            );

            if (!searchTerm) {
                return !isAlreadySelected;
            }

            return (medName.includes(searchTerm) ||
                    (med.DCI || '').toLowerCase().includes(searchTerm) ||
                    (med.nomCommercial || '').toLowerCase().includes(searchTerm)) &&
                !isAlreadySelected;
        }).slice(0, 15);

        const contraindicatedCount = analysisResult
            ? analysisResult.analyse.filter(item => item.resultat === 'contre-indiqué').length
            : 0;
        const alternativesCount = analysisResult
            ? Object.keys(analysisResult.alternatives || {}).length
            : 0;

        if (error) {
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '80vh',
                    background: '#f9fafb',
                    padding: '20px'
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '32px',
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        maxWidth: '500px',
                        width: '100%'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: '#fee2e2',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            color: '#dc2626',
                            fontSize: '28px'
                        }}>
                            ⚠️
                        </div>
                        <h3 style={{ color: '#dc2626', marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
                            Erreur de chargement
                        </h3>
                        <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' }}>
                            {error}
                        </p>
                        <button
                            onClick={() => this.loadData()}
                            style={{
                                background: 'var(--app-primary-color)',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                            <span>Réessayer</span>
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="multiparametric-container" style={{
                minHeight: '100vh',
                background: '#f9fafb',
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                <div style={{
                    maxWidth: '1600px',
                    margin: '0 auto',
                    padding: '28px 32px'
                }}>
                    {/* Header */}
                    {/* Selection Section */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '20px',
                        marginBottom: '24px'
                    }} className="mpa-selection-grid">
                        {/* Profils Card */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '26px',
                            boxShadow: '0 8px 24px rgba(31, 58, 69, 0.05)',
                            border: '1px solid #e5edf0',
                            position: 'relative'
                        }} className="mpa-selection-card">
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '20px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <div style={{
                                        width: '36px',
                                        height: '36px',
                                        background: '#ecfdf5',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#10b981'
                                    }}>
                                        <svg style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 style={{
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            color: '#111827',
                                            margin: 0
                                        }}>
                                            Profils du patient
                                        </h3>
                                        <p style={{
                                            fontSize: '12px',
                                            color: '#6b7280',
                                            margin: '4px 0 0 0'
                                        }}>
                                            Sélectionnez les profils du patient
                                        </p>
                                    </div>
                                </div>
                                <div style={{
                                    background: '#f3f4f6',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151'
                                }}>
                                    {selectedProfiles.length} sélectionné(s)
                                </div>
                            </div>

                            <div style={{ position: 'relative', marginBottom: '20px' }}>
                                <input
                                    ref={this.profileInputRef}
                                    className="mpa-search-input"
                                    type="text"
                                    placeholder="Rechercher un profil..."
                                    value={profileSearchText}
                                    onChange={(e) => {
                                        this.setState({
                                            profileSearchText: e.target.value,
                                            showProfileDropdown: true
                                        });
                                    }}
                                    onFocus={this.handleProfileInputFocus}
                                    onBlur={this.handleProfileInputBlur}
                                    style={{
                                        width: '100%',
                                        padding: '16px',
                                        paddingLeft: '40px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '10px',
                                        fontSize: '16px',
                                        outline: 'none',
                                        transition: 'all 0.2s ease',
                                        background: 'white'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af'
                                }}>
                                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                    </svg>
                                </div>
                            </div>

                            {showProfileDropdown && (
                                <div
                                    ref={this.profileDropdownRef}
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% - 20px)',
                                        left: '24px',
                                        right: '24px',
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        marginTop: '4px'
                                    }}
                                    onMouseDown={this.handleProfileDropdownMouseDown}
                                >
                                    {!profileOptions || profileOptions.length === 0 ? (
                                        <div style={{
                                            padding: '12px 16px',
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            textAlign: 'center'
                                        }}>
                                            Chargement des profils...
                                        </div>
                                    ) : filteredProfiles.length > 0 ? (
                                        filteredProfiles.map((profile, index) => (
                                            <div
                                                key={profile?.id || index}
                                                onClick={() => this.handleProfileSelection(profile)}
                                                style={{
                                                    padding: '12px 16px',
                                                    cursor: 'pointer',
                                                    borderBottom: index < filteredProfiles.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                    fontSize: '14px',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <span style={{ color: '#374151' }}>
                                                    {profile?.nom || profile?.name || profile?.profile || profile}
                                                </span>
                                                <span style={{
                                                    color: 'var(--app-primary-color)',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}>
                                                    Ajouter
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{
                                            padding: '12px 16px',
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            textAlign: 'center'
                                        }}>
                                            {profileSearchText ?
                                                `Aucun profil trouvé pour "${profileSearchText}"` :
                                                'Tapez pour rechercher un profil'
                                            }
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                minHeight: '40px'
                            }}>
                                {selectedProfiles.map((profile, index) => (
                                    <div key={index} style={{
                                        background: 'var(--app-primary-color)',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <span>{profile}</span>
                                        <button
                                            onClick={() => this.removeProfile(profile)}
                                            style={{
                                                background: 'rgba(255,255,255,0.2)',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '18px',
                                                height: '18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: 'white',
                                                fontSize: '12px',
                                                padding: 0,
                                                lineHeight: 1
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Médicaments Card */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '26px',
                            boxShadow: '0 8px 24px rgba(31, 58, 69, 0.05)',
                            border: '1px solid #e5edf0',
                            position: 'relative'
                        }} className="mpa-selection-card">
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '20px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <div style={{
                                        width: '36px',
                                        height: '36px',
                                        background: '#eff6ff',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--app-primary-color)'
                                    }}>
                                        <svg style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 style={{
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            color: '#111827',
                                            margin: 0
                                        }}>
                                            Médicaments prescrits
                                        </h3>
                                        <p style={{
                                            fontSize: '12px',
                                            color: '#6b7280',
                                            margin: '4px 0 0 0'
                                        }}>
                                            Sélectionnez les médicaments à analyser
                                        </p>
                                    </div>
                                </div>
                                <div style={{
                                    background: '#f3f4f6',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151'
                                }}>
                                    {selectedMedications.length} sélectionné(s)
                                </div>
                            </div>

                            <div style={{ position: 'relative', marginBottom: '20px' }}>
                                <input
                                    ref={this.medicationInputRef}
                                    className="mpa-search-input"
                                    type="text"
                                    placeholder="Rechercher un médicament..."
                                    value={medicationSearchText}
                                    onChange={(e) => {
                                        this.setState({
                                            medicationSearchText: e.target.value,
                                            showMedicationDropdown: true
                                        });
                                    }}
                                    onFocus={this.handleMedicationInputFocus}
                                    onBlur={this.handleMedicationInputBlur}
                                    style={{
                                        width: '100%',
                                        padding: '16px',
                                        paddingLeft: '40px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '10px',
                                        fontSize: '16px',
                                        outline: 'none',
                                        transition: 'all 0.2s ease',
                                        background: 'white'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af'
                                }}>
                                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                    </svg>
                                </div>
                            </div>

                            {showMedicationDropdown && (
                                <div
                                    ref={this.medicationDropdownRef}
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% - 20px)',
                                        left: '24px',
                                        right: '24px',
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        marginTop: '4px'
                                    }}
                                    onMouseDown={this.handleMedicationDropdownMouseDown}
                                >
                                    {this.state.medicationsLoading ? (
                                        <div style={{
                                            padding: '12px 16px',
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            textAlign: 'center'
                                        }}>
                                            Chargement des médicaments...
                                        </div>
                                    ) : !medicationOptions || medicationOptions.length === 0 ? (
                                        <div style={{
                                            padding: '16px',
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ marginBottom: '10px' }}>
                                                {this.state.medicationsError
                                                    ? 'Impossible de charger les médicaments. Vérifiez votre connexion.'
                                                    : 'Aucun médicament disponible.'}
                                            </div>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => this.loadMedications()}
                                                style={{
                                                    padding: '8px 16px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    background: 'white',
                                                    color: '#374151',
                                                    fontSize: '13px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Réessayer
                                            </button>
                                        </div>
                                    ) : filteredMedications.length > 0 ? (
                                        filteredMedications.map((medication, index) => (
                                            <div
                                                key={medication?._id || medication?.id || index}
                                                onClick={() => this.handleMedicationSelection(medication)}
                                                style={{
                                                    padding: '12px 16px',
                                                    cursor: 'pointer',
                                                    borderBottom: index < filteredMedications.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                    fontSize: '14px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                                    {medication?.nom || medication?.name || 'Médicament sans nom'}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', gap: '12px' }}>
                                                    {(medication?.DCI || medication?.dci) && <span>DCI: {medication.DCI || medication.dci}</span>}
                                                    {medication?.laboratoire && <span>Labo: {medication.laboratoire}</span>}
                                                    {medication?.forme && <span>Forme: {medication.forme}</span>}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{
                                            padding: '16px',
                                            textAlign: 'center',
                                            color: '#6b7280',
                                            fontSize: '14px'
                                        }}>
                                            {medicationSearchText ?
                                                `Aucun médicament trouvé pour "${medicationSearchText}"` :
                                                'Tapez pour rechercher un médicament'
                                            }
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                minHeight: '40px'
                            }}>
                                {selectedMedications.map((medication, index) => (
                                    <div key={index} style={{
                                        background: '#10b981',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <span>{medication?.nom || medication?.name}</span>
                                        <button
                                            onClick={() => this.removeMedication(medication?._id || medication?.id)}
                                            style={{
                                                background: 'rgba(255,255,255,0.2)',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '18px',
                                                height: '18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: 'white',
                                                fontSize: '12px',
                                                padding: 0,
                                                lineHeight: 1
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: '12px',
                        marginBottom: '32px'
                    }} className="mpa-actions">
                        <button
                            onClick={this.performAnalysis}
                            disabled={isAnalyzing || selectedProfiles.length === 0 || selectedMedications.length === 0}
                            style={{
                                background: selectedProfiles.length > 0 && selectedMedications.length > 0
                                    ? 'var(--app-primary-color)' : '#9ca3af',
                                color: 'white',
                                border: 'none',
                                padding: '14px 32px',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: selectedProfiles.length > 0 && selectedMedications.length > 0
                                    ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                minWidth: '200px',
                                justifyContent: 'center'
                            }}
                        >
                            {isAnalyzing ? (
                                <>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid rgba(255,255,255,0.3)',
                                        borderTopColor: 'white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <span>Analyse en cours...</span>
                                </>
                            ) : (
                                <>
                                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <span>Lancer l'analyse</span>
                                </>
                            )}
                        </button>

                        <button
                            onClick={this.newAnalysis}
                            style={{
                                background: '#f3f4f6',
                                color: '#374151',
                                border: '1px solid #d1d5db',
                                padding: '14px 32px',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                minWidth: '200px',
                                justifyContent: 'center'
                            }}
                        >
                            <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                            <span>Nouvelle analyse</span>
                        </button>
                    </div>

                    {/* Results Section */}
                    {analysisResult && (
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden'
                        }}>
                            {/* Results Header */}
                            <div style={{
                                padding: '24px',
                                borderBottom: '1px solid #e5e7eb',
                                background: 'var(--app-primary-color)',
                                color: '#073b46'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            background: 'rgba(255,255,255,0.5)',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                                                Résultats de l'analyse
                                            </h2>
                                            <p style={{ fontSize: '14px', opacity: 0.82, margin: '4px 0 0 0' }}>
                                                {new Date(analysisResult.timestamp).toLocaleDateString('fr-FR')} • {analysisResult.analyse.length} combinaisons analysées
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={this.toggleAlternatives}
                                        disabled={alternativesCount === 0}
                                        style={{
                                            background: 'rgba(255,255,255,0.58)',
                                            border: '1px solid rgba(7,59,70,0.16)',
                                            color: '#073b46',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            cursor: alternativesCount > 0 ? 'pointer' : 'not-allowed',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            opacity: alternativesCount > 0 ? 1 : 0.5
                                        }}
                                    >
                                        <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                                        </svg>
                                        <span>{this.state.showAlternatives ? 'Masquer les alternatives' : 'Voir les alternatives'}</span>
                                    </button>
                                </div>

                                {/* Stats */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: '20px'
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{
                                            fontSize: '32px',
                                            fontWeight: '700',
                                            marginBottom: '4px'
                                        }}>
                                            {analysisResult.analyse.length}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: '500' }}>
                                            COMBINAISONS
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{
                                            fontSize: '32px',
                                            fontWeight: '700',
                                            marginBottom: '4px'
                                        }}>
                                            {contraindicatedCount}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: '500' }}>
                                            CONTRE-INDICATIONS
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{
                                            fontSize: '32px',
                                            fontWeight: '700',
                                            marginBottom: '4px'
                                        }}>
                                            {alternativesCount}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: '500' }}>
                                            ALTERNATIVES
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Results Table */}
                            <div style={{
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse'
                                }}>
                                    <thead>
                                    <tr style={{
                                        background: '#f9fafb',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        <th style={{
                                            padding: '16px 20px',
                                            textAlign: 'left',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#374151',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>
                                            Profil
                                        </th>
                                        <th style={{
                                            padding: '16px 20px',
                                            textAlign: 'left',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#374151',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>
                                            Médicament
                                        </th>
                                        <th style={{
                                            padding: '16px 20px',
                                            textAlign: 'left',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#374151',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>
                                            Résultat
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {analysisResult.analyse.map((item, index) => (
                                        <tr key={index} style={{
                                            borderBottom: index < analysisResult.analyse.length - 1 ? '1px solid #f3f4f6' : 'none',
                                            background: index % 2 === 0 ? 'white' : '#f9fafb'
                                        }}>
                                            <td style={{
                                                padding: '16px 20px',
                                                fontSize: '14px',
                                                color: '#374151'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{
                                                        width: '6px',
                                                        height: '6px',
                                                        borderRadius: '50%',
                                                        background: 'var(--app-primary-color)'
                                                    }}></div>
                                                    {item.profil}
                                                </div>
                                            </td>
                                            <td style={{
                                                padding: '16px 20px',
                                                fontSize: '14px',
                                                color: '#374151',
                                                fontWeight: '500'
                                            }}>
                                                {item.medicament}
                                            </td>
                                            <td style={{
                                                padding: '16px 20px'
                                            }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '6px 12px',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        background: item.resultat === 'contre-indiqué'
                                                            ? '#fee2e2' : '#dcfce7',
                                                        color: item.resultat === 'contre-indiqué'
                                                            ? '#dc2626' : '#166534',
                                                        border: `1px solid ${item.resultat === 'contre-indiqué' ? '#fecaca' : '#bbf7d0'}`
                                                    }}>
                                                        {item.resultat === 'contre-indiqué' ? 'Contre-indiqué' : 'Compatible'}
                                                    </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Alternatives Section */}
                            {this.state.showAlternatives && alternativesCount > 0 && (
                                <div style={{
                                    padding: '24px',
                                    borderTop: '1px solid #e5e7eb',
                                    background: '#f9fafb'
                                }}>
                                    <h3 style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#111827',
                                        marginBottom: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <svg style={{ width: '18px', height: '18px', color: 'var(--app-primary-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                                        </svg>
                                        Alternatives disponibles ({alternativesCount})
                                    </h3>

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px'
                                    }}>
                                        {Object.keys(analysisResult.alternatives || {}).map(medicationName => {
                                            const alternative = analysisResult.alternatives[medicationName];
                                            return (
                                                <div key={medicationName} style={{
                                                    background: 'white',
                                                    borderRadius: '12px',
                                                    padding: '20px',
                                                    border: '1px solid #e5e7eb',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        marginBottom: '16px'
                                                    }}>
                                                        <div>
                                                            <h4 style={{
                                                                fontSize: '14px',
                                                                fontWeight: '600',
                                                                color: '#dc2626',
                                                                margin: '0 0 4px 0'
                                                            }}>
                                                                ❌ {medicationName}
                                                            </h4>
                                                            <p style={{
                                                                fontSize: '12px',
                                                                color: '#6b7280',
                                                                margin: 0
                                                            }}>
                                                                Contre-indiqué pour <strong>{alternative.profil_concerne}</strong>
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <h5 style={{
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            color: '#059669',
                                                            marginBottom: '12px'
                                                        }}>
                                                            Alternatives proposées ({alternative.equivalents_disponibles?.length || 0})
                                                        </h5>

                                                        {alternative.equivalents_disponibles?.length > 0 ? (
                                                            <div style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '12px'
                                                            }}>
                                                                {alternative.equivalents_disponibles.map((alt, idx) => (
                                                                    <div key={idx} style={{
                                                                        padding: '12px',
                                                                        borderRadius: '8px',
                                                                        background: '#f9f9f9',
                                                                        border: '1px solid #e5e7eb',
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: '15px',
                                                                        minHeight: '50px'
                                                                    }}>
                                                                        <div style={{ flex: '1 1 250px' }}>
                                                                            <span style={{
                                                                                fontSize: '13px',
                                                                                fontWeight: '600',
                                                                                color: '#111827'
                                                                            }}>
                                                                                {alt.nom}
                                                                            </span>
                                                                            <div style={{
                                                                                fontSize: '11px',
                                                                                color: '#6b7280',
                                                                                marginTop: '4px'
                                                                            }}>
                                                                                DCI: {alt.DCI || 'Non spécifié'}
                                                                            </div>
                                                                        </div>

                                                                        <div>
                                                                            <span style={{
                                                                                fontSize: '10px',
                                                                                fontWeight: '600',
                                                                                color: 'var(--app-primary-color)',
                                                                                background: 'var(--app-primary-tint)',
                                                                                padding: '4px 8px',
                                                                                borderRadius: '12px',
                                                                                border: '1px solid #bfdbfe'
                                                                            }}>
                                                                                {alt.type === 'equivalent' ? 'Équivalent' :
                                                                                    alt.type === 'meme_famille' ? 'Même famille' :
                                                                                        alt.type === 'meme_classe' ? 'Même classe' : 'Alternative'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                padding: '12px',
                                                                background: '#fef3c7',
                                                                borderRadius: '8px',
                                                                fontSize: '13px',
                                                                color: '#92400e',
                                                                textAlign: 'center',
                                                                border: '1px solid #fcd34d'
                                                            }}>
                                                                Aucune alternative disponible dans la base de données
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <style dangerouslySetInnerHTML={{
                    __html: `
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                        ::-webkit-scrollbar {
                            width: 6px;
                            height: 6px;
                        }
                        ::-webkit-scrollbar-track {
                            background: #f1f1f1;
                            border-radius: 3px;
                        }
                        ::-webkit-scrollbar-thumb {
                            background: #c1c1c1;
                            border-radius: 3px;
                        }
                        ::-webkit-scrollbar-thumb:hover {
                            background: #a8a8a8;
                        }
                    `
                }} />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const products = state.products?.products || [];
    const profiles = state.profiles?.profiles || [];
    const loading = state.status?.loading || false;

    return {
        products,
        profiles,
        loading
    };
};

const mapDispatchToProps = dispatch => ({
    fetchProducts: (filters, preferOffline) => dispatch(fetchProducts(filters, preferOffline)),
    getAllProducts: () => dispatch(getAllProducts()),
    fetchProfiles: (filters, preferOffline) => dispatch(fetchProfiles(filters, preferOffline)),
    showNotification: (message, status) => {
        dispatch({
            type: 'SET_MESSAGE',
            payload: { message, status }
        });
    }
});

MultiparametricAnalysisPage.propTypes = {
    products: PropTypes.array,
    profiles: PropTypes.array,
    loading: PropTypes.bool,
    fetchProducts: PropTypes.func.isRequired,
    getAllProducts: PropTypes.func,
    fetchProfiles: PropTypes.func.isRequired,
    showNotification: PropTypes.func.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(MultiparametricAnalysisPage);